import { useController, useFormContext, useWatch } from "react-hook-form";

import { Label } from "src/base-components/Label";
import { Option, Select } from "src/base-components/Select";
import {
  getStatusOptions,
  parseStatusKey,
  STATUS_KEY_SEPARATOR,
} from "src/cases/common/statusOptions";
import {
  CreateCaseFormValues,
  CreateCaseStep,
} from "src/cases/createCase/createCaseSteps";
import { useQueueMembers } from "src/cases/queues/hooks";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CasePropertiesValue } from "src/clients/case-management";
import { Avatar } from "src/design-system/Avatar";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useCurrentUserId } from "src/store/AuthStore";
import { getUserName } from "src/utils/user";

type CreateCaseRoutingStepProps = {
  statusProperty: CasePropertiesValue | undefined;
  queueId: string;
  step: CreateCaseStep;
};

export const CreateCaseRoutingStep: React.FC<CreateCaseRoutingStepProps> = ({
  statusProperty,
  queueId,
  step,
}) => {
  const { cases: casesCapabilities } = useCapabilities();
  const currentUserId = useCurrentUserId();
  const { data: queueMembers, isLoading: isLoadingMembers } =
    useQueueMembers(queueId);

  const statusOptions = getStatusOptions(statusProperty);

  const assigneeUsers = casesCapabilities.canAssign
    ? queueMembers
    : queueMembers.filter((user) => user.id === currentUserId);

  const assigneeOptions = assigneeUsers.map((user) => {
    const name = getUserName(user) ?? "Unknown user";
    return {
      key: user.id,
      searchText: name,
      value: (
        <span className="flex items-center gap-x-2">
          <Avatar user={user} />
          {name}
        </span>
      ),
    };
  });

  return (
    <FormStepWrapper description={step.description} title={step.formTitle}>
      <div className="flex flex-col gap-3">
        {statusOptions.length > 0 && (
          <div className="flex flex-col">
            <Label required>Status</Label>
            <StatusSelect statusOptions={statusOptions} />
          </div>
        )}
        <div className="flex flex-col">
          <Label>Assignee</Label>
          <AssigneeSelect
            assigneeOptions={assigneeOptions}
            isLoadingMembers={isLoadingMembers}
            searchable={casesCapabilities.canAssign}
          />
        </div>
      </div>
    </FormStepWrapper>
  );
};

const StatusSelect: React.FC<{ statusOptions: Option[] }> = ({
  statusOptions,
}) => {
  const form = useFormContext<CreateCaseFormValues>();
  useController({
    control: form.control,
    name: "status",
    rules: { required: true },
  });

  const status = useWatch({ control: form.control, name: "status" });
  const subStatus = useWatch({ control: form.control, name: "sub_status" });
  const currentStatusKey = status
    ? subStatus
      ? `${status}${STATUS_KEY_SEPARATOR}${subStatus}`
      : status
    : "";

  return (
    <Select
      dataLoc="create-case-status-dropdown"
      options={statusOptions}
      placeholder="Select a status"
      value={currentStatusKey}
      fullWidth
      onChange={(selectedKey) => {
        const { status, subStatus } = parseStatusKey(selectedKey);
        form.setValue("status", status, {
          shouldValidate: true,
          shouldDirty: true,
        });
        form.setValue("sub_status", subStatus, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }}
    />
  );
};

const AssigneeSelect: React.FC<{
  assigneeOptions: Option[];
  isLoadingMembers: boolean;
  searchable: boolean;
}> = ({ assigneeOptions, isLoadingMembers, searchable }) => {
  const form = useFormContext<CreateCaseFormValues>();
  const { field } = useController({
    control: form.control,
    name: "assignee_id",
  });

  const commonProps = {
    dataLoc: "create-case-assignee-dropdown",
    loading: isLoadingMembers,
    options: assigneeOptions,
    placeholder: "Select assignee",
    value: field.value,
    fullWidth: true as const,
    showResetButton: true as const,
    onChange: (value: Nullable<string>) => field.onChange(value),
  };

  return searchable ? (
    <Select {...commonProps} searchPlaceholder="Search user..." searchable />
  ) : (
    <Select {...commonProps} />
  );
};
